import React, { FC, useEffect, useMemo } from 'react';
import { resize, ResizeMode } from '@wix/thumbnail-generator';
import { captureException } from '@sentry/react';
import classnames from 'classnames';
import { pipe, propOr, replace } from 'ramda';
import {
  FileAwarenessMonitorErrorsList,
  FileAwarenessMonitorErrorTypeTag,
  withFileUsedParam,
} from '@wix/media-manager-shared';

import { BI_ERRORS, errorLoggerBiOnly } from '../../../services/loggers/errorLogger';
import { ImageLoadError, loadImage } from '../../../utils/dom/media';
import { imageLoad$ } from '../../../utils/fedops/imageLoadedAfterMediaStudioSave';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { toSecureUserBasedMediaUrl } from '../../../utils/hoc/withMMGRSettings/utils';
import { useBoolean } from '../../../utils/hooks/useBoolean';
import { normalizeUri } from '../../../utils/string';
import { withoutProtocol } from '../../../utils/url';
import { isSafari } from '../../../utils/userAgent';
import styles from './ResizedImage.scss';
import { IOuter } from './ResizedImage.types';
import { getHostBySource, shouldResizeForSource } from './utils';

const blueBackgroundStyle = { backgroundColor: '#f4fafe' };

export const ResizedImage: FC<IOuter> = ({
  className,
  children,
  dataHook = 'resized-image',
  onClick,
  src: srcFromProps,
  source,
  width,
  height,
  style,
  params,
  isSecure,
  siteMediaToken,
  isAllUserBasedMedia,
  shouldUseBackgroundImageImmediately = false,
}) => {
  const { value: shouldUseBackgroundImage, setTrue: startUseBackgroundImage } = useBoolean(
    shouldUseBackgroundImageImmediately,
  );

  const { mmgrSettings } = useMMGRSettings();
  const shouldKeepOriginalSize = propOr(false, 'keepOriginalSize', params);

  const { src, domStyles } = useMemo(() => {
    if (!srcFromProps) {
      return { src: srcFromProps, domStyles: { width, height, ...style, ...blueBackgroundStyle } };
    }

    if (shouldKeepOriginalSize || !shouldResizeForSource(source)) {
      return {
        src: srcFromProps,
        domStyles: {
          width,
          height,
          ...style,
          ...(shouldUseBackgroundImage ? { backgroundImage: `url("${srcFromProps}")` } : blueBackgroundStyle),
          ...(shouldKeepOriginalSize ? { backgroundSize: 'auto' } : {}),
        },
      };
    }

    const mode = propOr(ResizeMode.Fill, 'mode', params);

    const getParams =
      isSecure && !isAllUserBasedMedia ? `?site_token=${siteMediaToken || mmgrSettings.siteMediaToken}` : '';

    const baseHostToBeUsed = getHostBySource(source, isSecure, mmgrSettings);
    const baseHostToBeUsedWithoutProtocol = withoutProtocol(baseHostToBeUsed);

    const imageSrc = pipe(
      withoutProtocol,
      replace(baseHostToBeUsed, ''),
      replace(baseHostToBeUsedWithoutProtocol, ''),
    )(srcFromProps);

    const handleImageWithParam = (url?: string) =>
      withFileUsedParam(url, {
        logger: (error) =>
          captureException(error, {
            tags: {
              [FileAwarenessMonitorErrorTypeTag]: FileAwarenessMonitorErrorsList.FailedToAppendParameter,
            },
          }),
      });

    const baseHost =
      isAllUserBasedMedia && isSecure ? toSecureUserBasedMediaUrl(mmgrSettings.privateMediaRestAPI) : baseHostToBeUsed;
    const backgroundImage = handleImageWithParam(resize(imageSrc, { width, height, mode }));

    // safari can't do WEBP, replace URL with PNG
    const backgroundImageFixedSafariWebp =
      isSafari && backgroundImage.match(/webp$/i) ? `${backgroundImage}.png` : backgroundImage;
    const fullImageUrl = handleImageWithParam(
      normalizeUri(`${baseHost}${baseHost ? '/' : ''}${backgroundImageFixedSafariWebp}${getParams}`),
    );

    return {
      src: fullImageUrl,
      domStyles: {
        width,
        height,
        ...style,
        ...(backgroundImageFixedSafariWebp && shouldUseBackgroundImage
          ? { backgroundImage: `url("${fullImageUrl}")` }
          : blueBackgroundStyle),
      },
    };
  }, [
    srcFromProps,
    shouldUseBackgroundImage,
    source,
    width,
    height,
    style,
    params,
    isSecure,
    siteMediaToken,
    isAllUserBasedMedia,
  ]);

  useEffect(() => {
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const BASE_DELAY = 1000;
    const MAX_ATTEMPTS = 7;

    let loadImageHandle = null;
    let attempts = 0;
    let imageLoadingAborted = false;

    const loadImageRetry = (): void => {
      if (attempts > MAX_ATTEMPTS) {
        return;
      }

      loadImageHandle = loadImage(src);

      loadImageHandle.promise
        .then(() => {
          imageLoad$.next(src);
          startUseBackgroundImage();
        })
        .catch(async (reason: Error) => {
          if (reason.message === ImageLoadError.ImageLoadCancelled) {
            return;
          }

          if (reason.message === ImageLoadError.CannotLoadImage) {
            if (attempts > 0) {
              await delay(BASE_DELAY * Math.pow(2, attempts));
            }

            attempts++;
            loadImageHandle = null;

            if (!imageLoadingAborted) {
              loadImageRetry();
            }
          }

          errorLoggerBiOnly.log(BI_ERRORS.general.image404, { reason, p1: src });
        });
    };

    loadImageRetry();

    return () => {
      loadImageHandle?.abort();
      imageLoadingAborted = true;
    };
  }, [src, startUseBackgroundImage]);

  return (
    <div
      onClick={onClick}
      className={classnames(styles.resizedImage, className)}
      style={domStyles}
      data-hook={dataHook}
    >
      {children}
    </div>
  );
};
